import { Combobox, InputBase, useCombobox } from '@mantine/core'
import { Icon, Spinner } from '@percent/lemonade'
import type { SelectProps } from './select.types'
import classes from './select.module.scss'
import { useEffect, useMemo, useState } from 'react'
import { clsx } from '@mantine/styles'

export function Select({
  options,
  onChange,
  value,
  placeholder,
  clearable,
  searchable = true,
  isLoading,
  error,
}: SelectProps) {
  const selectedOption = value ? options.find((o) => o.value === value) : null
  const [search, setSearch] = useState(selectedOption?.label ?? '')
  const [dropdownOpened, setDropdownOpened] = useState(false)

  useEffect(() => {
    if (value && options.length > 0) {
      const opt = options.find((o) => o.value === value)
      setSearch(opt?.label ?? '')
    } else {
      setSearch('')
    }
  }, [value, options])

  const combobox = useCombobox({
    onDropdownOpen: () => {
      setDropdownOpened(true)
    },
    onDropdownClose: () => {
      setDropdownOpened(false)
      setSearch(selectedOption?.label ?? '')
      combobox.resetSelectedOption()
    },
  })

  const filteredOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options
    const query = search.toLowerCase().trim()
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.value.toLowerCase().includes(query)
    )
  }, [options, search, searchable])

  const handleOptionSubmit = (optValue: string) => {
    const option = options.find((o) => o.value === optValue)
    if (option) {
      setSearch(option.label)
      onChange(option.value, option)
    }
    combobox.closeDropdown()
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setSearch('')
    onChange(null, null)
    combobox.closeDropdown()
  }

  const handleBlur = () => {
    combobox.closeDropdown()
    setSearch(selectedOption?.label ?? '')
  }

  const rightSection = (
    <div className={classes.SelectTriggerRightSection}>
      {clearable && value && !isLoading && (
        <div
          className={classes.SelectTriggerClear}
          onClick={handleClear}
          onMouseDown={(e) => e.preventDefault()}
          role="button"
          tabIndex={-1}
          aria-label="Clear selection"
        >
          <Icon name="close" size={4} color="gray600" />
        </div>
      )}
      <div
        className={clsx(classes.SelectTriggerDropdown, {
          [classes.SelectTriggerDropdownOpen]: dropdownOpened,
        })}
        onClick={() => combobox.toggleDropdown()}
      >
        <Icon name="dropdown-arrow-down" size={8} color="gray600" />
      </div>
    </div>
  )

  return (
    <div className={classes.Select}>
      <Combobox
        store={combobox}
        onOptionSubmit={handleOptionSubmit}
        classNames={{ option: classes.Option, dropdown: classes.Dropdown }}
      >
        <Combobox.Target>
          <InputBase
            component="input"
            type="text"
            classNames={{
              root: classes.InputRoot,
              input: classes.Input,
            }}
            error={error}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value)
              combobox.openDropdown()
              combobox.updateSelectedOptionIndex()
            }}
            onFocus={() => combobox.openDropdown()}
            onBlur={handleBlur}
            onClick={() => combobox.openDropdown()}
            placeholder={placeholder}
            disabled={isLoading}
            leftSection={isLoading ? <Spinner size={4} /> : undefined}
            rightSection={rightSection}
            rightSectionWidth="4rem"
            rightSectionPointerEvents="all"
            readOnly={!searchable}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <Combobox.Option
                  value={opt.value}
                  key={opt.value}
                  active={opt.value === value}
                  aria-selected={opt.value === value}
                >
                  {opt.label}
                </Combobox.Option>
              ))
            ) : (
              <Combobox.Empty />
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  )
}
